import { useEffect, useRef, useState } from "react"
import { useLazyFetchSubscriptionsByIdsQuery } from "../../redux/queries"
import { AdvisorSession, SubscriptionDetails } from "../../types"

// Server caps a batch at 200 ids; stay well under it so a single slow
// chunk doesn't hold up the whole map.
export const SUBSCRIPTION_BATCH_SIZE = 50

const chunk = <T>(items: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size))
  }
  return out
}

export const useSubscriptionMap = (sessions: AdvisorSession[]) => {
  const [fetchByIds] = useLazyFetchSubscriptionsByIdsQuery()
  const fetchedIds = useRef(new Set<string>())
  const [subscriptionMap, setSubscriptionMap] = useState<
    Record<string, SubscriptionDetails>
  >({})

  useEffect(() => {
    const newIds = Array.from(
      new Set(
        sessions
          .map(s => s.subscriptionId)
          .filter((id): id is string => !!id && !fetchedIds.current.has(id))
      )
    )

    if (!newIds.length) return

    newIds.forEach(id => fetchedIds.current.add(id))
    Promise.all(
      chunk(newIds, SUBSCRIPTION_BATCH_SIZE).map(ids =>
        fetchByIds(ids)
          .unwrap()
          .catch((): SubscriptionDetails[] => [])
      )
    ).then(batches => {
      const updates: Record<string, SubscriptionDetails> = {}
      batches.flat().forEach(sub => {
        if (sub?.id) updates[String(sub.id)] = sub
      })
      if (Object.keys(updates).length) {
        setSubscriptionMap(prev => ({ ...prev, ...updates }))
      }
    })
  }, [sessions, fetchByIds])

  return subscriptionMap
}
